// Tiny celebratory sound effect using the Web Audio API (no asset / network needed).
// Mute preference persists in localStorage.

const MUTE_KEY = "bb_sfx_muted";

export function isMuted() {
  try { return localStorage.getItem(MUTE_KEY) === "1"; } catch { return false; }
}

export function setMuted(muted) {
  try { localStorage.setItem(MUTE_KEY, muted ? "1" : "0"); } catch { /* ignore */ }
}

let ctx = null;
function audioCtx() {
  if (typeof window === "undefined") return null;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  if (!ctx) ctx = new AC();
  if (ctx.state === "suspended") ctx.resume();
  return ctx;
}

// Cheerful rising arpeggio, followed by a little sparkle.
export function playUnlockChime() {
  if (isMuted()) return;
  const ac = audioCtx();
  if (!ac) return;
  const now = ac.currentTime;
  const notes = [523.25, 659.25, 783.99, 1046.5]; // C5 E5 G5 C6
  notes.forEach((freq, i) => {
    const osc = ac.createOscillator();
    const gain = ac.createGain();
    osc.type = "triangle";
    osc.frequency.value = freq;
    const t = now + i * 0.1;
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(0.25, t + 0.03);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.35);
    osc.connect(gain).connect(ac.destination);
    osc.start(t);
    osc.stop(t + 0.4);
  });
  // sparkle
  const s = ac.createOscillator();
  const sg = ac.createGain();
  s.type = "sine";
  s.frequency.setValueAtTime(1568, now + 0.45); // G6
  sg.gain.setValueAtTime(0.0001, now + 0.45);
  sg.gain.exponentialRampToValueAtTime(0.18, now + 0.48);
  sg.gain.exponentialRampToValueAtTime(0.0001, now + 0.75);
  s.connect(sg).connect(ac.destination);
  s.start(now + 0.45);
  s.stop(now + 0.8);
}

const soundOn = () => { try { return !isMuted() && localStorage.getItem("aa_voice_on") !== "off"; } catch { return true; } };

function tone(ac, freq, t, dur, vol = 0.22, type = "triangle") {
  const o = ac.createOscillator();
  const g = ac.createGain();
  o.type = type;
  o.frequency.setValueAtTime(freq, t);
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(vol, t + 0.02);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  o.connect(g).connect(ac.destination);
  o.start(t);
  o.stop(t + dur + 0.05);
}

// crowd-like cheering: band-passed noise that swells and flutters
function crowd(ac, t, dur, vol) {
  const len = Math.floor(ac.sampleRate * dur);
  const buf = ac.createBuffer(1, len, ac.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * (0.6 + 0.4 * Math.sin(i / (ac.sampleRate / 9)));
  const src = ac.createBufferSource();
  src.buffer = buf;
  const bp = ac.createBiquadFilter();
  bp.type = "bandpass";
  bp.frequency.value = 1400;
  bp.Q.value = 0.7;
  const g = ac.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(vol, t + 0.35);
  g.gain.setValueAtTime(vol, t + dur * 0.6);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  src.connect(bp).connect(g).connect(ac.destination);
  src.start(t);
}

export function playCorrectDing() {
  if (!soundOn()) return;
  const ac = audioCtx();
  if (!ac) return;
  const t = ac.currentTime;
  tone(ac, 880, t, 0.18, 0.25, "sine");
  tone(ac, 1318.5, t + 0.09, 0.35, 0.25, "sine");
}

export function playLevelCheer() {
  if (!soundOn()) return;
  const ac = audioCtx();
  if (!ac) return;
  const t = ac.currentTime;
  [523.25, 659.25, 783.99, 1046.5, 783.99, 1046.5].forEach((f, i) => tone(ac, f, t + i * 0.11, 0.3));
  crowd(ac, t + 0.2, 1.4, 0.18);
}

export function playEncourage() {
  if (!soundOn()) return;
  const ac = audioCtx();
  if (!ac) return;
  const t = ac.currentTime;
  [392, 440, 523.25].forEach((f, i) => tone(ac, f, t + i * 0.16, 0.3, 0.18, "sine"));
}

export function playBigCheers() {
  if (!soundOn()) return;
  const ac = audioCtx();
  if (!ac) return;
  const t = ac.currentTime;
  const fanfare = [[523.25, 0], [523.25, 0.15], [523.25, 0.3], [659.25, 0.45], [783.99, 0.75], [659.25, 0.95], [783.99, 1.1], [1046.5, 1.3]];
  fanfare.forEach(([f, d]) => { tone(ac, f, t + d, 0.35, 0.22, "square"); tone(ac, f / 2, t + d, 0.35, 0.12); });
  crowd(ac, t + 0.3, 3.2, 0.3);
  [0.6, 1.0, 1.5, 2.1].forEach((d) => tone(ac, 1568 + Math.random() * 400, t + d, 0.2, 0.12, "sine"));
}
